import React, { PureComponent } from 'react';
import { Chart } from 'react-google-charts';
import * as KSLPlusAPI from "../services/KSLPlusAPI";

// Datenassistent im Chatfenster: Darstellung von Tabellen und Diagrammen aus der Antwort von api/Ai/daten.
// Zahlen, Einheiten und Diagrammart kommen fertig vom Server, hier wird nur gezeichnet.

// Der geöffnete Plan, wie in der Adresse der Visualisierung
export type DatenRahmen = {
    kunde: number,
    gemeinde: number,
    jahr: number,
    plantyp: number,
    planstufe: number,
    verwaltung: boolean,
    uhConfig: number
}

// Feste Farben; die Farben der Gemeinde sind noch nicht angebunden
const FARBEN = ["#23507d", "#8fb3d9", "#5b8c5a", "#c9a227", "#7a5195", "#ef8a62", "#4d9de0", "#9c6644", "#6c757d", "#2a9d8f"];

function farbeArt(art?: string): string {
    return art === "Ansatz" ? "#8fb3d9" : art === "Abweichung" || art === "Veraenderung" ? "#c0504d" : "#23507d";
}

// Zahlen im Tooltip: deutsches Format mit Einheit, € ohne Nachkommastellen
function zahlFormat(spalte: number, einheit?: string) {
    return {
        type: "NumberFormat" as "NumberFormat",
        column: spalte,
        options: { decimalSymbol: ",", groupingSymbol: ".", fractionDigits: einheit === "€" ? 0 : 2, suffix: einheit ? " " + einheit : "" }
    };
}

type Zeichnung = {
    chartType: any,
    data: any[],
    options: any,
    formatters: { type: "NumberFormat", column: number, options: {} }[],
    hoehe: number,
    legende: { name: string, farbe: string }[]      // nur Torte, sonst leer
}

function zeichnung(d: KSLPlusAPI.DiagrammDaten): Zeichnung {
    const kategorien = d.kategorien || [];
    const reihen = d.reihen || [];
    const werte = (r: KSLPlusAPI.DiagrammReihe) => r.werte || [];
    // Gezeichnet wird der gerundete Wert, der Tooltip zeigt den genauen Betrag wie in der Tabelle (anzeige vom Server)
    const wert = (r: KSLPlusAPI.DiagrammReihe, i: number): any => {
        const w = werte(r)[i];
        const a = r.anzeige ? r.anzeige[i] : undefined;
        return w != null && a ? { v: w, f: a } : w;
    };
    // Mit genauem Text kein Zahlenformat, sonst überschriebe es den Text
    const genau = (r?: KSLPlusAPI.DiagrammReihe) => !!(r && r.anzeige && r.anzeige.length > 0);

    // Wunschfarben zuerst, leere Einträge behalten die Standardfarbe
    const wunsch = d.farben || [];
    const farben = wunsch.length > 0
        ? wunsch.map((f, i) => f || FARBEN[i % FARBEN.length]).concat(FARBEN.slice(wunsch.length))
        : FARBEN;

    // Achse ab 0, außer der Nutzer wünscht einen anderen Bereich
    const achse: any = { title: d.einheit, minValue: 0, viewWindowMode: "maximized" };
    if (d.achseVon != null || d.achseBis != null) {
        achse.viewWindowMode = "explicit";
        achse.viewWindow = {};
        if (d.achseVon != null) achse.viewWindow.min = d.achseVon;
        if (d.achseBis != null) achse.viewWindow.max = d.achseBis;
        delete achse.minValue;
    }

    let opt: any = { legend: { position: "bottom" }, chartArea: { left: 70, right: 15, top: 20, bottom: 60 }, fontName: "Segoe UI", colors: farben };
    const data: any[] = [];
    const formate: Zeichnung["formatters"] = [];
    let typ = "ColumnChart";
    let hoehe = 300;
    let legende: Zeichnung["legende"] = [];

    if (d.art === "torte") {
        data.push([{ type: "string", label: "Teil" }, { type: "number", label: reihen[0].name }]);
        kategorien.forEach((k, i) => data.push([k, wert(reihen[0], i)]));
        if (!genau(reihen[0])) formate.push(zahlFormat(1, d.einheit));
        typ = "PieChart";
        // Eigene Legende unter dem Diagramm: Die von Google Charts steht unten in nur einer Zeile und blättert.
        // Ohne Schwelle zeichnet Google Charts auch sehr kleine Teile, sonst passten Farben und Legende nicht mehr zusammen.
        opt = { legend: { position: "none" }, pieSliceText: "percentage", sliceVisibilityThreshold: 0, chartArea: { width: "90%", height: "90%" }, fontName: "Segoe UI", colors: farben };
        legende = kategorien.map((k, i) => ({ name: k, farbe: farben[i % farben.length] }));
    }
    else if (d.art === "wasserfall") {
        // Gestapelte Säulen: unsichtbarer Sockel bis zum Stand davor, darauf der sichtbare Schritt.
        // Über und unter 0 stapelt Google Charts getrennt; ein Schritt, der die 0 kreuzt, wird in beide Teile zerlegt.
        const legende = d.legende || ["Summe", "Anstieg", "Rückgang"];
        const von = d.von || [];
        const bis = d.bis || [];
        const arten = d.kategorieArten || [];
        const kopf: any[] = [{ type: "string", label: "Schritt" }, { type: "number", label: "Sockel" }];
        legende.forEach(n => { kopf.push({ type: "number", label: n }); kopf.push({ type: "number", label: n + " " }); });
        data.push(kopf);
        kategorien.forEach((k, i) => {
            const lo = Math.min(von[i], bis[i]);
            const hi = Math.max(von[i], bis[i]);
            let sockel: number | null = null, oben: number | null = null, unten: number | null = null;
            if (lo >= 0) { sockel = lo; oben = hi - lo; }
            else if (hi <= 0) { sockel = hi; unten = lo - hi; }
            else { oben = hi; unten = lo; }
            const zeile: any[] = [k, sockel, null, null, null, null, null, null];
            const s = 2 + 2 * Math.max(0, ["summe", "anstieg", "rueckgang"].indexOf(arten[i]));
            // Tooltip mit dem genauen Betrag des Schritts, auch wenn er in zwei Teile zerlegt ist
            const text = genau(reihen[0]) ? reihen[0].anzeige![i] : undefined;
            zeile[s] = oben != null && text ? { v: oben, f: text } : oben;
            zeile[s + 1] = unten != null && text ? { v: unten, f: text } : unten;
            data.push(zeile);
        });
        if (!genau(reihen[0])) for (let s = 1; s <= 7; s++) formate.push(zahlFormat(s, d.einheit));
        opt.isStacked = true;
        opt.legend = { position: "top" };
        opt.chartArea = { left: 110, right: 15, top: 40, bottom: 120 };     // links Platz für die schrägen Namen
        opt.hAxis = { slantedText: true, slantedTextAngle: 35, maxTextLines: 1, textStyle: { fontSize: 11 } };
        opt.vAxis = { ...achse, baseline: 0, baselineColor: "#16202c" };
        hoehe = 420;
        // Farben für Summe, Anstieg und Rückgang
        const f = [wunsch[0] || FARBEN[0], wunsch[1] || FARBEN[3], wunsch[2] || FARBEN[1]];
        opt.series = { 0: { color: "transparent", enableInteractivity: false, visibleInLegend: false } };
        f.forEach((farbe, j) => {
            opt.series[1 + 2 * j] = { color: farbe };
            opt.series[2 + 2 * j] = { color: farbe, visibleInLegend: false };
        });
    }
    else if (d.art === "kombi") {
        // Säulen für die Beträge, Linie für die Veränderung zum Vorjahr in % auf der rechten Achse
        data.push([{ type: "string", label: "Jahr" }, { type: "number", label: reihen[0].name }, { type: "number", label: reihen[1].name }]);
        kategorien.forEach((k, i) => data.push([k, wert(reihen[0], i), werte(reihen[1])[i]]));
        if (!genau(reihen[0])) formate.push(zahlFormat(1, d.einheit));
        formate.push(zahlFormat(2, "%"));
        typ = "ComboChart";
        opt.seriesType = "bars";
        opt.series = { 0: { color: wunsch[0] || FARBEN[0] }, 1: { type: "line", targetAxisIndex: 1, pointSize: 6, color: wunsch[1] || FARBEN[3] } };
        opt.vAxes = { 0: achse, 1: { title: "%", format: "#,##0.#" } };
        opt.chartArea.right = 50;
    }
    else {
        const arten = d.kategorieArten || [];
        if (reihen.length === 1 && arten.length === kategorien.length) {
            // Eine Position über mehrere Spalten: Ansatz und Abweichung in eigener Farbe
            data.push([{ type: "string", label: "Kategorie" }, { type: "number", label: reihen[0].name }, { role: "style" }]);
            kategorien.forEach((k, i) => data.push([k, wert(reihen[0], i),
                (wunsch.length > 0 ? wunsch[Math.min(i, wunsch.length - 1)] : "") || farbeArt(arten[i])]));
            opt.legend = { position: "none" };
        }
        else {
            data.push([{ type: "string", label: "Kategorie" } as any].concat(reihen.map(r => ({ type: "number", label: r.name }))));
            kategorien.forEach((k, i) => data.push([k as any].concat(reihen.map(r => wert(r, i)))));
        }
        reihen.forEach((r, i) => { if (!genau(r)) formate.push(zahlFormat(i + 1, d.einheit)); });

        const form = reihen.length > 0 ? reihen[0].form : "";
        typ = form === "linie" ? "LineChart" : d.waagerecht ? "BarChart" : "ColumnChart";
        if (d.gestapelt) opt.isStacked = true;
        if (typ === "LineChart") opt.pointSize = 6;
        // Viele Reihen: Legende oben in bis zu drei Zeilen
        if (reihen.length > 3) {
            opt.legend = { position: "top", maxLines: 3 };
            opt.chartArea.top = 60;
            opt.chartArea.bottom = 40;
        }
        opt[d.waagerecht ? "hAxis" : "vAxis"] = achse;
        if (d.waagerecht) {
            // Balken: links Platz für die Namen, Höhe nach Anzahl der Balken
            opt.chartArea.left = "40%";
            hoehe = Math.max(260, 60 + kategorien.length * reihen.length * 22);
        }
    }

    return { chartType: typ, data: data, options: opt, formatters: formate, hoehe: hoehe, legende: legende };
}


type BloeckeProps = {
    bloecke: KSLPlusAPI.AntwortBlock[],
    abgefragt?: string[]
}

type DiagrammProps = {
    d: KSLPlusAPI.DiagrammDaten
}

// Ein Diagramm zum Auf- und Zuklappen, anfangs offen. Zugeklappt wird es nicht gezeichnet,
// beim Aufklappen zeichnet Google Charts es in der aktuellen Breite neu.
class KIDatenDiagramm extends PureComponent<DiagrammProps, { offen: boolean }> {
    private rahmen = React.createRef<HTMLElement>();
    private wrapper: any = null;        // ChartWrapper von Google Charts, solange das Diagramm gezeichnet ist
    private beobachter: any = null;     // ResizeObserver
    private breite = 0;
    private zeichenTimer = 0;

    constructor(props: DiagrammProps) {
        super(props);
        this.state = { offen: true };
    }

    // Google Charts zeichnet von selbst nur neu, wenn sich das Browserfenster ändert. Ändert sich nur das Chatfenster
    // (Maximieren, Ziehen am Griff), zeichnet das Diagramm hier neu und nutzt so die ganze Breite.
    componentDidMount() {
        const Beobachter = (window as any).ResizeObserver;
        if (!Beobachter || !this.rahmen.current) return;
        this.breite = this.rahmen.current.clientWidth;
        this.beobachter = new Beobachter(this.breiteGeaendert);
        this.beobachter.observe(this.rahmen.current);
    }

    componentWillUnmount() {
        window.clearTimeout(this.zeichenTimer);
        if (this.beobachter) this.beobachter.disconnect();
    }

    breiteGeaendert = () => {
        const rahmen = this.rahmen.current;
        if (!rahmen || rahmen.clientWidth === this.breite) return;
        this.breite = rahmen.clientWidth;
        window.clearTimeout(this.zeichenTimer);
        this.zeichenTimer = window.setTimeout(() => {
            if (this.wrapper) this.wrapper.draw();
        }, 150);
    }

    umschalten = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (this.state.offen) this.wrapper = null;
        this.setState({ offen: !this.state.offen });
    }

    render() {
        const d = this.props.d;
        const z = this.state.offen ? zeichnung(d) : null;
        return (
            <details className="kidaten-diagramm" open={this.state.offen} ref={this.rahmen}>
                <summary onClick={this.umschalten}>Diagramm{d.titel ? ": " + d.titel : ""}</summary>
                {z &&
                    <Chart
                        width="100%"
                        height={z.hoehe + "px"}
                        chartType={z.chartType}
                        chartLanguage="de"
                        loader={<div className="kidaten-laden">Diagramm wird geladen …</div>}
                        data={z.data}
                        options={z.options}
                        formatters={z.formatters}
                        getChartWrapper={w => { this.wrapper = w; }} />}
                {z && z.legende.length > 0 &&
                    <div className="kidaten-legende">
                        {z.legende.map((l, i) => <span key={i}><i style={{ background: l.farbe }} />{l.name}</span>)}
                    </div>}
                {z && d.hinweise && d.hinweise.length > 0 &&
                    <div className="kidaten-hinweis">{d.hinweise.join(" ")}</div>}
            </details>
        );
    }
}

// Tabellen, Diagramme und die Quelle unter dem Antworttext.
// PureComponent: zeichnet nur neu, wenn sich die Antwort ändert, nicht bei jeder Eingabe im Textfeld.
export class KIDatenBloecke extends PureComponent<BloeckeProps> {

    render() {
        // Mit Diagramm ist die Tabelle zugeklappt
        const mitDiagramm = this.props.bloecke.some(b => b.typ === "diagramm" && !!b.diagramm);
        // Ohne interne Kennung (T1), ohne Handbuch-Suchen (deren Quellen stehen schon als [1], [2] unter dem Text)
        // und ohne die Formeln von rechnen
        const abgefragt = (this.props.abgefragt || [])
            .map(a => a.replace(/^T\d+: /, ""))
            .filter(a => !a.startsWith("Handbuch (") && !a.startsWith("Rechnung von KSLplus"));

        return (
            <div className="kidaten">
                {this.props.bloecke.map((b, i) => {
                    if (b.typ === "diagramm" && b.diagramm)
                        return <KIDatenDiagramm key={"d" + i} d={b.diagramm} />;
                    if (b.typ === "tabelle" && b.tabelle)
                        return this.tabelle(b.tabelle, i, !mitDiagramm);
                    return null;
                })}
                {abgefragt.length > 0 &&
                    <details className="kidaten-abgefragt">
                        <summary>Quelle</summary>
                        {abgefragt.map((a, i) => <div key={i}>{a}</div>)}
                    </details>}
            </div>
        );
    }

    tabelle(t: KSLPlusAPI.ErgebnisTabelle, i: number, offen: boolean) {
        const spalten = t.spalten || [];
        const zahl = (k: number) => spalten[k] && spalten[k].typ !== "text" ? "kidaten-zahl" : "";
        return (
            <details className="kidaten-tabelle" key={"t" + i} open={offen}>
                <summary>Tabelle: {t.titel}</summary>
                <div className="kidaten-rollen">
                    <table>
                        <thead>
                            <tr>{spalten.map((s, k) => <th key={k} className={zahl(k)}>{s.titel}</th>)}</tr>
                        </thead>
                        <tbody>
                            {(t.zeilen || []).map((z, j) =>
                                <tr key={j}>{z.map((c, k) => <td key={k} className={zahl(k)}>{c.anzeige}</td>)}</tr>)}
                        </tbody>
                    </table>
                </div>
                {t.hinweise && t.hinweise.length > 0 &&
                    <div className="kidaten-hinweis">{t.hinweise.join(" ")}</div>}
            </details>
        );
    }
}